import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SmartLink from '../common/SmartLink'
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../lib/supabaseClient';
import { useRegisterEditor } from '../lib/EditorContext';
import { defaultContent } from '../lib/defaultContent';

const { FiAlertOctagon, FiCheckCircle, FiDollarSign, FiPlayCircle } = FiIcons;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const defaultSections = [
  {
    id: 2,
    title: "Lies vs. Truths",
    iconName: "FiCheckCircle",
    desc: "Lie: 'If I were better, this wouldn't happen.' Truth: 'Challenging behavior ≠ parenting failure.' We break down the toxic myths holding you back.",
    iconBg: "bg-blue-100",
    iconBorder: "border-blue-200",
    iconColor: "text-blue-600"
  },
  {
    id: 3,
    title: "Your Priceless Value",
    iconName: "FiDollarSign",
    desc: "Carpool driver, chef, emotional regulator, peacekeeper. Calculate the hidden labor of parenting and recognize the immense, priceless worth of your role.",
    iconBg: "bg-yellow-100",
    iconBorder: "border-yellow-200",
    iconColor: "text-yellow-600"
  },
  {
    id: 1,
    title: "Navigating Terrible Advice",
    iconName: "FiAlertOctagon",
    desc: "'Just be stricter.' 'It's a phase.' 'My kid never did that.' Learn to filter out the noise and trust your intuition as the expert on your child.",
    iconBg: "bg-red-100",
    iconBorder: "border-red-200",
    iconColor: "text-brand-rose"
  }
];

const adviceExamples = [
  "“Just be stricter”",
  "“It’s a phase”",
  "“My kid never did that”",
  "“Have you tried a reward chart?”",
  "“Pray about it”",
  "“Take away screens”"
];

const liesAndTruths = [
  { lie: "If I were better, this wouldn't happen.", truth: "Challenging behavior ≠ parenting failure" },
  { lie: "Other families don't deal with this.", truth: "Many struggle silently. You're not alone." },
  { lie: "I should stay calm every time.", truth: "Repair matters more than perfection." }
];

const pricelessRoles = [
  { role: "Carpool driver", rate: "$20/hr" },
  { role: "Cook/meal planner", rate: "$25/hr" },
  { role: "Emotional regulator", rate: "$50/hr" },
  { role: "Your Worth", rate: "Priceless" }
];

export default function LiesPriceAdvice() {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from('page_content')
          .select('content')
          .eq('page_id', 'lies-price-advice')
          .single();

        if (error) {
          console.warn('Could not fetch lies-price-advice content, using defaults.', error.message);
        } else if (data && data.content) {
          setContent(data.content);
        }
      } catch (err) {
        console.error('Error fetching lies-price-advice content:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContent();
  }, []);

  const title = content?.title || "Lies, Price, & Terrible Advice";
  const subtitle = content?.subtitle || "Unpack the unsolicited opinions, reject the parental guilt, and discover the true value of the hard work you put in every single day.";
  const overline = content?.overline || "Reclaim Your Confidence";

  const currentSections = content?.sections || defaultSections;
  const currentLiesAndTruths = content?.liesAndTruths || liesAndTruths;
  const currentPricelessRoles = content?.pricelessRoles || pricelessRoles;
  const currentAdviceExamples = content?.adviceExamples || adviceExamples;

  const terribleAdviceTitle = content?.terribleAdviceTitle || defaultContent["lies-price-advice"].terribleAdviceTitle;
  const wantToLearnMore = content?.wantToLearnMore || defaultContent["lies-price-advice"].wantToLearnMore;

  const handleSaveSuccess = (newContent) => {
    setContent(newContent);
  };

  useRegisterEditor(
    'lies-price-advice',
    { title, subtitle, overline, sections: currentSections, adviceExamples: currentAdviceExamples, liesAndTruths: currentLiesAndTruths, pricelessRoles: currentPricelessRoles, terribleAdviceTitle, wantToLearnMore },
    handleSaveSuccess,
    'Lies, Price, Advice Page'
  );

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" exit="hidden" className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#333] dark:text-[#fff] font-bold tracking-widest uppercase text-xs mb-3 block">{overline}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-[#333] dark:text-[#fff] mb-6">{title}</h1>
          <p className="text-lg text-[#333] dark:text-[#fff]">
            {subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {currentSections.map((section) => (
            <motion.div key={section.id} variants={fadeUp} className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100 flex flex-col items-center text-center hover:shadow-lg transition-all duration-300">
              <div className={`w-16 h-16 ${section.iconBg} dark:bg-brand-slate rounded-full flex items-center justify-center mb-6 shadow-sm border ${section.iconBorder}`}>
                <SafeIcon icon={FiIcons[section.iconName] || FiIcons.FiAlertOctagon} className={`${section.iconColor} w-8 h-8`} />
              </div>
              <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-4">{section.title}</h3>
              <p className="text-[#333] dark:text-[#fff] leading-relaxed">{section.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div variants={fadeUp} className="mb-20 max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          <div className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100 md:col-span-2">
            <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-6 flex items-center gap-3">
              <span className="text-3xl">🎭</span> Lies vs. Truths
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {currentLiesAndTruths.map((item, idx) => (
                <div key={idx} className="bg-sand-50 dark:bg-brand-slate/50 p-6 rounded-xl border border-sand-100 relative">
                  <div className="mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-clay mb-1 block">The Lie</span>
                    <p className="text-[#333] dark:text-[#fff] font-medium opacity-80 line-through decoration-brand-rose decoration-2">"{item.lie}"</p>
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-green-600 mb-1 block">The Truth</span>
                    <p className="text-[#333] dark:text-[#fff] font-bold text-lg">"{item.truth}"</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100">
            <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-6 flex items-center gap-3">
              <span className="text-3xl">💰</span> Your Priceless Value
            </h3>
            <div className="space-y-4">
              {currentPricelessRoles.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center border-b border-sand-100 pb-2 last:border-0 last:pb-0">
                  <span className="text-[#333] dark:text-[#fff] font-medium">{item.role}</span>
                  <span className="text-brand-rose font-bold">{item.rate}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100">
            <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-6 flex items-center gap-3">
              <span className="text-3xl">📢</span> {terribleAdviceTitle}
            </h3>
            <div className="flex flex-wrap gap-3">
              {currentAdviceExamples.map((advice, idx) => (
                <div key={idx} className="bg-sand-50 dark:bg-brand-slate/50 px-4 py-2 rounded-lg text-[#333] dark:text-[#fff] font-medium border border-sand-100">
                  {advice}
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="bg-brand-rose text-white rounded-[3rem] p-10 md:p-16 text-center max-w-4xl mx-auto shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">{wantToLearnMore.title}</h2>
            <p className="text-lg mb-10 max-w-2xl mx-auto opacity-90">
              {wantToLearnMore.description}
            </p>
            <SmartLink to={wantToLearnMore.buttonLink} className="inline-flex items-center gap-2 px-10 py-4 bg-white text-brand-rose rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-lg">
              <SafeIcon icon={FiPlayCircle} /> {wantToLearnMore.buttonText}
            </SmartLink>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}